import { useEffect, useMemo, useState } from 'react'
import { useLocation } from 'react-router'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { ensureI18nNamespacesLoaded, AUTHORIZED_SHELL_I18N_NAMESPACES } from 'i18nProduct'
import { isPrivateAccess } from 'utils/routeAccess'
import { selectAppUser } from 'modules/App/store/selectors'
import { TITLE_SEPARATOR } from 'components/Seo/PageTitle'
import { getLastMatchedRoute } from 'utils/findMatchedRoutes'

const buildTitle = (labelKeys, t, i18n) => {
  if (!labelKeys.length) {
    return null
  }

  if (!labelKeys.every((key) => i18n.exists(key))) {
    return null
  }

  const parts = labelKeys.map((key) => t(key)).filter(Boolean)

  return parts.length ? parts.reverse().join(TITLE_SEPARATOR) : null
}

const useResolvedPageTitle = (matchedRoutes) => {
  const { pathname } = useLocation()
  const { t, i18n } = useTranslation(undefined, { bindI18nStore: 'added removed' })
  const user = useSelector(selectAppUser)
  const [routeI18nReady, setRouteI18nReady] = useState(false)

  const matchedRoute = getLastMatchedRoute(matchedRoutes)
  const labelKeys = useMemo(() => {
    if (matchedRoute?.page_title_keys?.length) {
      return matchedRoute.page_title_keys
    }

    const routes = Array.isArray(matchedRoutes) ? matchedRoutes : []
    const leafLabelKey = [...routes].reverse().find((route) => route.label_key)?.label_key

    return leafLabelKey ? [leafLabelKey] : []
  }, [matchedRoutes, matchedRoute?.page_title_keys])

  const isPrivateRoute = useMemo(
    () => isPrivateAccess(matchedRoute?.access),
    [matchedRoute?.access],
  )

  const shellI18nNamespaces = useMemo(
    () => (user?.id && isPrivateRoute ? AUTHORIZED_SHELL_I18N_NAMESPACES : []),
    [user?.id, isPrivateRoute],
  )

  useEffect(() => {
    let mounted = true
    setRouteI18nReady(false)

    const loadPromise = matchedRoute?.key
      ? ensureI18nNamespacesLoaded({
          routeKey: matchedRoute.key,
          routePath: matchedRoute.path,
          pathname,
          extraNamespaces: shellI18nNamespaces,
        })
      : Promise.resolve()

    loadPromise
      .then(() => {
        if (mounted) {
          setRouteI18nReady(true)
        }
      })
      .catch(() => {
        if (mounted) {
          setRouteI18nReady(true)
        }
      })

    return () => {
      mounted = false
    }
  }, [matchedRoute?.key, matchedRoute?.path, pathname, shellI18nNamespaces])

  return useMemo(() => {
    if (!routeI18nReady) {
      return null
    }

    return buildTitle(labelKeys, t, i18n)
  }, [routeI18nReady, labelKeys, t, i18n, i18n.language])
}

export default useResolvedPageTitle
